import React from 'react';
import Decimal from 'decimal.js';
import { OrderBook, TokenInfo } from '../types';

interface Props {
    orderBook: OrderBook | null;
    token: TokenInfo;
}

function fmtPrice(s: string, significantDigits = 8): string {
    try {
        const d = new Decimal(s);
        if (d.eq(0)) return '—';
        return d.toSignificantDigits(significantDigits).toFixed();
    } catch {
        return s;
    }
}

function fmtQty(s: string): string {
    try {
        const d = new Decimal(s);
        if (d.eq(0)) return '0';
        return d.toSignificantDigits(6).toString();
    } catch {
        return s;
    }
}

function vsMid(price: string, mid: string): string {
    try {
        const p = new Decimal(price);
        const m = new Decimal(mid);
        if (m.eq(0) || p.eq(0)) return '—';
        const bps = p.minus(m).div(m).mul(100);
        const sign = bps.gte(0) ? '+' : '';
        return `${sign}${bps.toFixed(3)}%`;
    } catch {
        return '—';
    }
}

export default function OrderBookView({ orderBook, token }: Props) {
    if (!orderBook) {
        return (
            <div className="card">
                <h2>Order Book — {token.symbol}</h2>
                <div className="empty">no data yet</div>
            </div>
        );
    }
    const {
        bids,
        asks,
        midPrice,
        spread,
        active,
        bestBid,
        bestAsk,
        timestamp,
    } = orderBook;
    const haveMidPrice = new Decimal(midPrice || '0').gt(0);
    const haveLevels = bids.length > 0 || asks.length > 0;
    const crossed =
        new Decimal(bestBid || '0').gt(0) &&
        new Decimal(bestAsk || '0').gt(0) &&
        new Decimal(bestBid).gt(bestAsk);

    // Pad to 8 levels each so the table doesn't reflow as depth changes.
    const padded = (xs: typeof bids) => {
        const out = xs.slice(0, 8);
        while (out.length < 8) {
            out.push({ price: '0', priceOffset: '0', quantity: '0', total: '0', tif: 0 });
        }
        return out;
    };
    // Asks: shown deepest first (highest price at top), best ask just above the spread bar.
    const asksTopDown = padded([...asks]).reverse();
    const bidsTopDown = padded([...bids]);

    const spreadPct = haveMidPrice
        ? new Decimal(spread).div(midPrice).mul(100)
        : null;
    const spreadPctText = spreadPct
        ? `${spreadPct.gte(0) ? '+' : ''}${spreadPct.toFixed(3)}%`
        : null;

    return (
        <div className="card">
            <h2>
                Order Book — {token.symbol}/USD{' '}
                <span className={active ? 'tag ok' : 'tag err'}>
                    {active ? 'active' : 'inactive'}
                </span>
            </h2>

            {haveMidPrice ? (
                <div className={`ob-summary ${crossed ? 'crossed' : ''}`}>
                    <span>
                        Spread: <b>{fmtPrice(spread)}</b>{' '}
                        <span className="muted">({spreadPctText})</span>
                        {crossed && <strong className="crossed-label"> CROSSED</strong>}
                    </span>
                    <span>
                        Mid Price: <b>{fmtPrice(midPrice)}</b>
                    </span>
                </div>
            ) : (
                <div className="ob-summary muted">
                    {haveLevels
                        ? 'one-sided book — mid and spread unavailable'
                        : 'no visible levels'}
                </div>
            )}

            <div className="ob-section">
                <h3>Asks</h3>
                <table className="ob-table">
                    <thead>
                        <tr>
                            <th>Price (USD per {token.symbol})</th>
                            <th>Size ({token.symbol})</th>
                            <th>Total ({token.symbol})</th>
                            <th>vs Mid (%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {asksTopDown.map((l, i) => {
                            const isFiller = l.quantity === '0';
                            return (
                                <tr key={`ask-${i}`} className={isFiller ? 'filler' : ''}>
                                    <td className="price-ask">
                                        {fmtPrice(l.price)}
                                    </td>
                                    <td>{isFiller ? '' : fmtQty(l.quantity)}</td>
                                    <td>{isFiller ? '' : fmtQty(l.total)}</td>
                                    <td className="vs-mid up">
                                        {haveMidPrice && !isFiller
                                            ? vsMid(l.price, midPrice)
                                            : ''}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className={`ob-spread-bar ${crossed ? 'crossed' : ''}`}>
                {haveMidPrice ? (
                    <>
                        Spread: <b>{fmtPrice(spread)}</b>{' '}
                        <span className="muted">({spreadPctText})</span>
                        {crossed && <strong className="crossed-label"> CROSSED</strong>}
                        {' · '}best bid{' '}
                        {fmtPrice(bestBid)} / best ask {fmtPrice(bestAsk)}
                    </>
                ) : (
                    <span className="muted">
                        {haveLevels ? 'one-sided book — no mid/spread' : 'no visible levels'}
                    </span>
                )}
            </div>

            <div className="ob-section">
                <h3>Bids</h3>
                <table className="ob-table">
                    <thead>
                        <tr>
                            <th>Price (USD per {token.symbol})</th>
                            <th>Size ({token.symbol})</th>
                            <th>Total ({token.symbol})</th>
                            <th>vs Mid (%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bidsTopDown.map((l, i) => {
                            const isFiller = l.quantity === '0';
                            return (
                                <tr key={`bid-${i}`} className={isFiller ? 'filler' : ''}>
                                    <td className="price-bid">
                                        {fmtPrice(l.price)}
                                    </td>
                                    <td>{isFiller ? '' : fmtQty(l.quantity)}</td>
                                    <td>{isFiller ? '' : fmtQty(l.total)}</td>
                                    <td className="vs-mid down">
                                        {haveMidPrice && !isFiller
                                            ? vsMid(l.price, midPrice)
                                            : ''}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="meta">
                {new Date(timestamp).toLocaleTimeString()}
            </div>
        </div>
    );
}
